import { injectable } from "tsyringe";
import { DashboardApiRepository } from "@/app/infrastructures/repositories/api/DashboardApiRepository";
import {
  CodDashboardDetailCourier,
  CutOffConsoleData,
  DashboardConsoleData,
  DashboardDetailList,
  DataCorporateDashboard,
  DetailCourierPerStatusCodDashboardData,
  ListCourierPartnerCodDashboard,
  SummaryCodDashboardData,
  ReferralAffiliate,
  SummaryCourierPartnerCodDashboard,
  PerformanceEmbedData,
} from "@/domain/entities/Dashboard";
import {
  DashboardConsoleRequest,
  DashboardDetailListRequest,
  DashboardDownloadRequest,
  DownloadSummaryCodRequest,
  CodDashboardRequest,
  CodDashboardCourierDetailRequest,
  CorporateDashboardRequest,
  CourierDailyDepositRequest,
} from "@/data/payload/api/DashboardRequests";
import {
  RequestList,
  ResponsePayload,
  ResponsePayloadV2,
} from "@/domain/entities/ResponsePayload";
import { string } from "zod/v4";

@injectable()
export class DashboardPresenter {
  private repository: DashboardApiRepository;

  constructor(repo: DashboardApiRepository) {
    this.repository = repo;
  }

  public getConsoleData(
    params: DashboardConsoleRequest
  ): Promise<DashboardConsoleData> {
    return this.repository.getConsoleData(params);
  }

  public getCutOffConsoleOutgoingStt(): Promise<CutOffConsoleData> {
    return this.repository.getCutOffConsoleOutgoingStt();
  }

  public getDashboardDetailList(
    params: DashboardDetailListRequest
  ): Promise<DashboardDetailList> {
    return this.repository.getDashboardDetailList(params);
  }

  public getDownloadDashboard(
    params: DashboardDownloadRequest
  ): Promise<ResponsePayload> {
    return this.repository.getDownloadDashboard(params);
  }

  public getListPosFollowUpDashboard(
    params: RequestList
  ): Promise<ResponsePayloadV2> {
    return this.repository.getListPosFollowUpDashboard(params);
  }

  public getDownloadDetailPOS(
    params: DashboardDetailListRequest
  ): Promise<any> {
    return this.repository.getDownloadDetailPOS(params);
  }

  public getListCourierPartnerCodDashboard(
    params: CodDashboardRequest
  ): Promise<ListCourierPartnerCodDashboard> {
    return this.repository.getListCourierPartnerCodDashboard(params);
  }

  public getListCourierPartnerCodDashboardV2(
    params: CodDashboardRequest
  ): Promise<ListCourierPartnerCodDashboard> {
    return this.repository.getListCourierPartnerCodDashboardV2(params);
  }

  public getListCourierPartnerCodDashboardV3(
    params: CodDashboardRequest,
    version: string
  ): Promise<ListCourierPartnerCodDashboard> {
    return this.repository.getListCourierPartnerCodDashboardV3(params, version);
  }

  public getSummarryPOSCodDashboard(
    params: string,
    date: any,
    version: string
  ): Promise<SummaryCourierPartnerCodDashboard> {
    return this.repository.getSummarryPOSCodDashboard(params, date, version);
  }

  public getSummaryCodDashboardData(
    path: string,
    params: CodDashboardRequest
  ): Promise<SummaryCodDashboardData> {
    return this.repository.getSummaryCodDashboardData(path, params);
  }

  public getDetailCourierPerStatusCodDashboardData(
    params: CodDashboardCourierDetailRequest
  ): Promise<DetailCourierPerStatusCodDashboardData[]> {
    return this.repository.getDetailCourierPerStatusCodDashboardData(params);
  }

  public postCourierDailyDeposit(
    type: string,
    payload: CourierDailyDepositRequest
  ): Promise<ResponsePayload> {
    return this.repository.postCourierDailyDeposit(type, payload);
  }

  public postCourierDailyDepositV3(
    type: string,
    payload: CourierDailyDepositRequest
  ): Promise<ResponsePayload> {
    return this.repository.postCourierDailyDepositV3(type, payload);
  }

  public async getSummaryDownload(
    type: string,
    filter: DownloadSummaryCodRequest,
    version: string
  ): Promise<any> {
    return this.repository.getSummaryDownload(type, filter, version);
  }

  public getDetailCourierData(
    params: CodDashboardCourierDetailRequest
  ): Promise<CodDashboardDetailCourier> {
    return this.repository.getCourierDetailData(params);
  }

  public getDetailCourierDownloadCodDashboardData(
    driverPhone: string,
    filter: CodDashboardCourierDetailRequest,
    isPic = false,
    version: string,
  ): Promise<any> {
    return this.repository.getDetailCourierDownloadCodDashboardData(
      driverPhone,
      filter,
      isPic,
      version
    );
  }

  public getCorporateDashboardData(
    path: string,
    params: CorporateDashboardRequest
  ): Promise<DataCorporateDashboard> {
    return this.repository.getCorporateDashboardData(path, params);
  }

  public getReferralAffiliate(): Promise<ReferralAffiliate> {
    return this.repository.getReferralAffiliate();
  }

  public getPerformanceEmbed(): Promise<PerformanceEmbedData> {
    return this.repository.getPerformanceEmbed();
  }
}
